/**
 * Strapi API Client
 */

const STRAPI_URL = process.env.STRAPI_URL || 'http://strapi:1337';
const PUBLIC_STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL || 'http://api.test.tst.md';
const STRAPI_TOKEN = process.env.STRAPI_API_TOKEN;

console.log('Strapi Client Initialized - Internal URL:', STRAPI_URL);
console.log('Strapi Client Initialized - Public URL:', PUBLIC_STRAPI_URL);
console.log('Strapi Token Present:', !!STRAPI_TOKEN);

/**
 * Базовый интерфейс ответа Strapi v5
 */
interface StrapiResponse<T> {
  data: T;
  meta: {
    pagination?: {
      total: number;
    };
  };
}

export interface StrapiImage {
  url: string;
  name: string;
  alternativeText?: string;
}

/**
 * Интерфейс Услуги (Service) - Старая коллекция (для обратной совместимости или если нужно)
 */
export interface Service {
  id: number;
  documentId: string;
  Title: string;
  Slug: string;
  Icon: string;
  Description?: string;
}

/**
 * Интерфейс Описания Услуги (ServiceDescription) - Новая коллекция
 */
export interface ServiceDescription {
  id: number;
  documentId: string;
  name: string;
  shot: any[]; // Rich text blocks
  long: any[]; // Rich text blocks
  slug: string;
  Img?: {
    url: string;
    alternativeText?: string;
  }[] | any; // Strapi returns media objects
}

/**
 * Интерфейс Страницы Услуг (ServicesPage) - Single Type
 */
export interface ServicesPage {
  Title: string;
  PageTitle: string;
  MetaDescription: any[];
  Keywords: string;
}

/**
 * Интерфейс Hero секции (HeroSection) - Single Type
 */
export interface HeroSection {
  StaticTitle: string;
  AnimatedPhrase: { phrase_text: string | null }[];
  SubTitle: string;
  TabletLine1: string;
  TabletLine2: string;
  TabletLine3: string;
  CTAText?: string;
  SecondaryCTAText?: string;
  HeroImage?: {
    url: string;
    alternativeText?: string;
  };
}

/**
 * Универсальная функция для запросов к Strapi
 */
async function fetchStrapi<T>(endpoint: string, locale: string = 'ru', params: Record<string, string> = {}): Promise<T | null> {
  const url = new URL(`${STRAPI_URL}${endpoint}`);
  url.searchParams.set('locale', locale);
  
  // Добавляем дополнительные параметры (например, populate)
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (STRAPI_TOKEN) {
    headers['Authorization'] = `Bearer ${STRAPI_TOKEN}`;
  }

  try {
    const res = await fetch(url.toString(), {
      headers,
      next: { revalidate: 0 },
    });

    if (!res.ok) {
      // Игнорируем ошибки отсутствия контента или прав (404/403) для предотвращения спама в консоли,
      // так как эти данные могут быть еще не созданы в Strapi.
      if (res.status === 404 || res.status === 403 || (res.status === 400 && endpoint.includes('hero-section'))) {
        return null;
      }
      console.error(`Strapi error: ${res.status} ${res.statusText} at ${url.toString()}`);
      return null;
    }

    const json: StrapiResponse<T> = await res.json();
    console.log(`Fetched ${Array.isArray(json.data) ? json.data.length : '1'} items from ${endpoint}`);
    return json.data;
  } catch (error) {
    console.error('Fetch error:', error);
    return null;
  }
}

/**
 * Получение всех описаний услуг (Service_description)
 */
export async function getServiceDescriptions(locale: string): Promise<ServiceDescription[]> {
  const data = await fetchStrapi<ServiceDescription[]>('/api/service-descriptions', locale, {
    'populate': 'Img'
  });
  return data || [];
}

/**
 * Получение данных страницы услуг (Single Type)
 */
export async function getServicesPage(locale: string): Promise<ServicesPage | null> {
  return await fetchStrapi<ServicesPage>('/api/services-page', locale);
}

/**
 * Получение данных для Hero секции
 */
export async function getHeroSection(locale: string): Promise<HeroSection | null> {
  return await fetchStrapi<HeroSection>('/api/hero-section', locale, {
    'populate': '*'
  });
}

// Старая функция для совместимости
export async function getServices(locale: string): Promise<Service[]> {
  const data = await fetchStrapi<Service[]>('/api/services', locale);
  return data || [];
}

/**
 * Хелпер для получения полного URL изображения
 */
export function getStrapiMedia(url: string | undefined) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('//')) return url;
  
  // Используем ПУБЛИЧНЫЙ URL для картинок, чтобы Next/Image не блокировал внутренние IP
  return `${PUBLIC_STRAPI_URL}${url}`;
}
